import { useCallback, useEffect, useState } from "react";
import { Building2, FolderOpen } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import {
  dashboardPathFor,
  isBuildingOnlyPath,
  isProjectsOnlyPath,
} from "@/components/layout/buildingViewRoutes";
import { ProjectPickerPanel } from "@/components/building/ProjectPickerPanel";
import { useCreateProjectDialog } from "@/domains/projects/components/createProjectDialogContext";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { useBuildingScope } from "@/hooks/buildings/useBuildingScope";
import { useBuildingViewMode } from "@/hooks/buildings/useBuildingViewMode";
import type { BuildingViewMode } from "@/hooks/buildings/useBuildingViewMode";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { SidebarTooltip } from "../atoms/SidebarTooltip";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

interface SidebarViewModeToggleProps {
  buildingId: string;
  /** Whether the sidebar is collapsed (icon-only rendering). */
  isCollapsed: boolean;
}

/**
 * The "Building / Projects" view-mode toggle rendered above the sidebar nav.
 *
 * Switching modes swaps the nav item set and how pages scope their data
 * (whole building vs one specific project). On switch:
 *  - if the current page doesn't exist in the target mode, navigate to that
 *    mode's dashboard;
 *  - switching to Projects opens the project dropdown anchored to
 *    the toggle (unless the building has exactly one project, which is
 *    entered directly) — Projects view works within one concrete project and the
 *    previous choice is deliberately not remembered, so the mode only flips
 *    once a project is picked (or immediately when the building has no
 *    projects at all, landing on the create-first empty state);
 *  - re-clicking the active Projects half re-opens the dropdown to switch
 *    projects — in both layouts, since the collapsed switch's two halves are
 *    buttons carrying their own mode just like the expanded pill's halves;
 *  - switching to Building on a shared page strips the `?project` param
 *    immediately so the page never renders one frame still project-scoped
 *    (`useBuildingScope` then forgets the scope entirely).
 */
export const SidebarViewModeToggle = observer(function SidebarViewModeToggle({
  buildingId,
  isCollapsed,
}: SidebarViewModeToggleProps) {
  const { t } = useLanguage();
  const { viewMode, setViewMode } = useBuildingViewMode(buildingId);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    selectedProjectId,
    setProject,
    projectOptions,
    optionsLoaded,
    optionsError,
    reloadOptions,
  } = useBuildingScope(buildingId);
  const { openCreateProject, canCreateProjects } = useCreateProjectDialog();
  const [pickerOpen, setPickerOpen] = useState(false);

  const enterProjectsMode = useCallback(() => {
    setViewMode("projects");
    if (isBuildingOnlyPath(location.pathname, buildingId)) {
      navigate(dashboardPathFor("projects", buildingId));
    }
    // Only reached when there is no project to pick — shared pages stay put
    // and render Projects view's create-first empty state.
  }, [setViewMode, location.pathname, buildingId, navigate]);

  const handleChange = (next: BuildingViewMode) => {
    if (next === viewMode) {
      // Already in Projects view: re-open the dropdown to switch projects.
      // Available in either layout — the context line's crumb chevron is the
      // other way in, and neither is the only one.
      if (next === "projects") setPickerOpen(true);
      return;
    }

    if (next === "projects") {
      if (optionsLoaded && projectOptions.length === 0) {
        // Nothing to pick — Projects view shows the create-first empty state.
        enterProjectsMode();
        return;
      }
      if (optionsLoaded && projectOptions.length === 1) {
        // Nothing to choose between — go straight into the only project.
        handlePickProject(projectOptions[0].id);
        return;
      }
      // Always pick a project from the dropdown before the mode flips — the
      // previous selection is deliberately not remembered.
      setPickerOpen(true);
      return;
    }

    setViewMode(next);
    if (isProjectsOnlyPath(location.pathname, buildingId)) {
      navigate(dashboardPathFor("building", buildingId));
      return;
    }
    if (searchParams.has("project")) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("project");
      setSearchParams(nextParams, { replace: true });
    }
  };

  const handlePickProject = (projectId: number) => {
    setPickerOpen(false);
    // Entering Projects view, landing on its dashboard from a building-only
    // route, and dropping a stale `folderId` are all `setProject`'s job — it
    // reads the live location through a ref, which is what this callback needs:
    // the create-project dialog captured it when it opened, so the route it
    // acts on is the one it is called on, not the one current back then.
    setProject(projectId);
  };

  // "New project" from the dropdown: close the popover first so the shared
  // dialog is never a child of it. Entering Projects view on the project that
  // gets created is the provider's follow-through — the same `setProject`
  // handlePickProject runs for an existing project, taken from the layout so
  // it cannot be left holding a stale location.
  const handleCreateProject = () => {
    setPickerOpen(false);
    openCreateProject();
  };

  // If the options finish loading empty while the dropdown is open (the user
  // clicked before the load resolved), there is nothing to pick — fall
  // through to Projects view, which shows the create-first empty state.
  // Likewise a single project resolves the pick by itself — but only while
  // ENTERING Projects view. Re-opening the dropdown from the active Projects
  // half is a deliberate request for it (to switch, or for "New project").
  const soleProjectId = projectOptions.length === 1 ? projectOptions[0].id : null;
  useEffect(() => {
    if (!pickerOpen || !optionsLoaded || projectOptions.length > 1) return;
    if (soleProjectId != null) {
      if (viewMode === "projects") return;
      setPickerOpen(false);
      setProject(soleProjectId);
      return;
    }
    setPickerOpen(false);
    enterProjectsMode();
  }, [
    pickerOpen,
    optionsLoaded,
    projectOptions.length,
    soleProjectId,
    viewMode,
    setProject,
    enterProjectsMode,
  ]);

  const groupLabel = `${t.buildings.sidebar.viewBuilding} / ${t.buildings.sidebar.viewProjects}`;
  const options: Array<{ value: BuildingViewMode; label: string; icon: typeof Building2 }> = [
    { value: "building", label: t.buildings.sidebar.viewBuilding, icon: Building2 },
    { value: "projects", label: t.buildings.sidebar.viewProjects, icon: FolderOpen },
  ];

  // The project dropdown, anchored to the toggle and opening into the
  // content area beside it — used both for the first selection and for
  // switching projects from the active Projects half. Its body is the shared
  // panel, so this dropdown and the context line's switcher stay identical
  // through the loading and load-failed branches too.
  const pickerContent = (
    <PopoverContent side="right" align="start" sideOffset={12} className="w-[280px] p-0">
      <ProjectPickerPanel
        projects={projectOptions}
        selectedProjectId={selectedProjectId}
        optionsLoaded={optionsLoaded}
        optionsError={optionsError}
        onReload={reloadOptions}
        onSelect={handlePickProject}
        onCreateProject={canCreateProjects ? handleCreateProject : undefined}
      />
    </PopoverContent>
  );

  if (isCollapsed) {
    // On/off switch: a small track with a red knob that carries the active
    // mode's icon and slides between the two ends; the inactive mode's icon
    // sits faintly at the far end, and a caption under the track names the
    // active mode.
    //
    // It looks like one switch but is two buttons, each covering its half of
    // the track and carrying its own mode, exactly like the expanded pill's two
    // halves — so pressing the knob while in Projects view re-opens the project
    // dropdown. A single target carrying only the inactive mode left a
    // collapsed sidebar with no way to switch project at all: the picker was
    // reachable only after expanding.
    const activeOption = options.find((option) => option.value === viewMode) ?? options[0];
    const ActiveIcon = activeOption.icon;
    return (
      <Popover open={pickerOpen} onOpenChange={setPickerOpen} modal={true}>
        <PopoverAnchor asChild>
          {/* h-10 matches the expanded pill (p-1 + py-1.5 + a text-sm line) so the
              nav items below sit at the same height in both sidebar states. */}
          <div
            role="group"
            aria-label={groupLabel}
            className="mx-auto flex h-10 w-12 shrink-0 flex-col items-center justify-center gap-0.5"
            {...pageInfoTarget("layout.viewMode")}
          >
            {/* White track with a hairline and a soft lift — the project tile's
                surface (`projectTile`), not a grey fill. */}
            <div className="relative h-[26px] w-[46px] rounded-full border border-black/[0.06] bg-white shadow-lift">
              <span
                aria-hidden="true"
                className={cn(
                  "absolute left-px top-px flex h-[22px] w-[22px] items-center justify-center rounded-full",
                  "bg-primary text-primary-foreground shadow-sm transition-transform duration-200 ease-out",
                  viewMode === "projects" && "translate-x-[20px]"
                )}
              >
                <ActiveIcon className="h-3 w-3 shrink-0" />
              </span>
              {options.map((option) => {
                const isActive = viewMode === option.value;
                return (
                  <SidebarTooltip key={option.value} content={option.label}>
                    <button
                      type="button"
                      aria-label={option.label}
                      aria-pressed={isActive}
                      aria-haspopup={option.value === "projects" ? "dialog" : undefined}
                      aria-expanded={option.value === "projects" ? pickerOpen : undefined}
                      onClick={() => handleChange(option.value)}
                      // Targets are 32px tall and run 1px past the track's
                      // ends; the focus ring is inset so the column never
                      // clips it. All in px: rem is 14.4px here, which left
                      // the halves short of the knob.
                      className={cn(
                        "absolute -top-[4px] flex h-[32px] items-center justify-center rounded-full text-muted-foreground",
                        // In the 44px track the knob spans 1-23 or 21-43. The
                        // active half is 28px wide (-1-27 or 17-45), so it
                        // covers the whole knob and sits above the 24px
                        // inactive half: a click anywhere on the knob is the knob's.
                        isActive ? "z-20 w-[28px]" : "z-10 w-[24px]",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                        option.value === "building" ? "-left-px" : "-right-px"
                      )}
                    >
                      {/* The active end is covered by the knob, which draws its icon. */}
                      {!isActive && <option.icon className="h-[11px] w-[11px] shrink-0" />}
                    </button>
                  </SidebarTooltip>
                );
              })}
            </div>
            <span
              aria-hidden="true"
              className="max-w-full truncate text-[9px] font-medium leading-[11px] text-muted-foreground"
            >
              {activeOption.label}
            </span>
          </div>
        </PopoverAnchor>
        {pickerContent}
      </Popover>
    );
  }

  // Slider-style pill on a white track: only the red thumb (white label) and
  // the inactive black label are visible, gliding between the two halves.
  // The thumb is width 50%-minus-track-padding anchored at left-1, so
  // translate-x-full lands it exactly on the second half.
  return (
    <Popover open={pickerOpen} onOpenChange={setPickerOpen} modal={true}>
      <div
        role="group"
        aria-label={groupLabel}
        className="relative grid w-full grid-cols-2 rounded-full bg-background p-1"
        {...pageInfoTarget("layout.viewMode")}
      >
        <span
          aria-hidden="true"
          className={cn(
            "absolute inset-y-1 left-1 w-[calc(50%-0.25rem)] rounded-full bg-primary shadow-sm",
            "transition-transform duration-200 ease-out",
            viewMode === "projects" && "translate-x-full"
          )}
        />
        {options.map(({ value, label, icon: Icon }) => {
          const optionButton = (
            <button
              key={value}
              type="button"
              aria-pressed={viewMode === value}
              aria-haspopup={value === "projects" ? "dialog" : undefined}
              aria-expanded={value === "projects" ? pickerOpen : undefined}
              onClick={() => handleChange(value)}
              className={cn(
                "relative z-10 flex items-center justify-center gap-1.5 rounded-full py-1.5 text-sm font-medium",
                "transition-colors duration-200",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                viewMode === value ? "text-primary-foreground" : "text-foreground"
              )}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{label}</span>
            </button>
          );
          // The dropdown anchors to the Projects half so it opens right
          // beside the button that was clicked.
          return value === "projects" ? (
            <PopoverAnchor asChild key={value}>
              {optionButton}
            </PopoverAnchor>
          ) : (
            optionButton
          );
        })}
      </div>
      {pickerContent}
    </Popover>
  );
});
